/** TanStack Query hooks for the analytics endpoints + filter serialization. */
import { useQuery } from "@tanstack/react-query";

import { ApiPaths, api } from "@/lib/api";

import type {
  AiPerformanceResp,
  CostResp,
  InspectionsResp,
  KpiFilterState,
  MeResp,
  MetricMap,
  OverviewResp,
  PerformanceScore,
  RealtimeTiles,
  TimeseriesResp,
} from "./types";

/** Filter → query params (drops empty values). */
export function toParams(f: KpiFilterState): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.date_from) out.date_from = f.date_from;
  if (f.date_to) out.date_to = f.date_to;
  if (f.operator_type) out.operator_type = f.operator_type;
  if (f.country) out.country = f.country;
  return out;
}

/** A stable key fragment so each filter combination caches separately. */
function keyOf(f: KpiFilterState): string {
  return JSON.stringify(toParams(f));
}

function get<T>(path: string, f: KpiFilterState, extra?: Record<string, string>) {
  return async () =>
    (await api.get<T>(path, { params: { ...toParams(f), ...(extra ?? {}) } })).data;
}

export function useOverview(f: KpiFilterState, enabled: boolean) {
  return useQuery({
    queryKey: ["analytics", "overview", keyOf(f)],
    queryFn: get<OverviewResp>(ApiPaths.analyticsOverview, f),
    enabled,
    refetchInterval: 60_000,
    placeholderData: (p) => p,
  });
}

export function useRealtime(f: KpiFilterState) {
  return useQuery({
    queryKey: ["analytics", "realtime", keyOf(f)],
    queryFn: get<RealtimeTiles>(ApiPaths.analyticsRealtime, f),
    refetchInterval: 30_000,
    placeholderData: (p) => p,
  });
}

export function useInspections(f: KpiFilterState, enabled: boolean) {
  return useQuery({
    queryKey: ["analytics", "inspections", keyOf(f)],
    queryFn: get<InspectionsResp>(ApiPaths.analyticsInspections, f),
    enabled,
    placeholderData: (p) => p,
  });
}

export function useAiCalling(f: KpiFilterState, enabled: boolean) {
  return useQuery({
    queryKey: ["analytics", "ai-calling", keyOf(f)],
    queryFn: get<MetricMap>(ApiPaths.analyticsAiCalling, f),
    enabled,
    placeholderData: (p) => p,
  });
}

export function useAiPerformance(f: KpiFilterState, enabled: boolean) {
  return useQuery({
    queryKey: ["analytics", "ai-performance", keyOf(f)],
    queryFn: get<AiPerformanceResp>(ApiPaths.analyticsAiPerformance, f),
    enabled,
    placeholderData: (p) => p,
  });
}

export function useScore(f: KpiFilterState, enabled: boolean) {
  return useQuery({
    queryKey: ["analytics", "score", keyOf(f)],
    queryFn: get<PerformanceScore>(ApiPaths.analyticsScore, f),
    enabled,
    placeholderData: (p) => p,
  });
}

export function useCost(f: KpiFilterState, enabled: boolean) {
  return useQuery({
    queryKey: ["analytics", "cost", keyOf(f)],
    queryFn: get<CostResp>(ApiPaths.analyticsCost, f),
    enabled,
    placeholderData: (p) => p,
  });
}

export function useTimeseries(
  f: KpiFilterState,
  metric: string,
  granularity: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["analytics", "timeseries", metric, granularity, keyOf(f)],
    queryFn: get<TimeseriesResp>(ApiPaths.analyticsTimeseries, f, {
      metric,
      granularity,
    }),
    enabled,
    placeholderData: (p) => p,
  });
}

export function useMe(f: KpiFilterState) {
  return useQuery({
    queryKey: ["analytics", "me", keyOf(f)],
    queryFn: get<MeResp>(ApiPaths.analyticsMe, f),
    placeholderData: (p) => p,
  });
}

/** Trigger a file download from the export endpoint (auth via axios blob). */
export async function downloadExport(
  f: KpiFilterState,
  fmt: "xlsx" | "csv" | "pdf",
): Promise<void> {
  const resp = await api.get(ApiPaths.analyticsExport, {
    params: { ...toParams(f), fmt },
    responseType: "blob",
  });
  const url = URL.createObjectURL(resp.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `nexbasira-kpis.${fmt}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
